import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { entries, fromEntries } from "remeda";
import { useAuthentication } from "../auth";
import {
  selectCloudRendering,
  selectLatencyTestCompleted,
} from "../features/cloudRenderingSlice";
import { getOrderedCloudRenderingRegions } from "../session-management-api";
import { useAppSelector } from "./redux";
import { CloudRenderingRegion, RegionFilter } from "./types";
import { useUserPreferencesQuery } from "./useUserPreferences";
import { useUseXRGatewaySettingQuery } from "./useUseXRGatewaySettingQuery";

export interface Region extends CloudRenderingRegion {
  latencyMs?: number;
}

/** @public */
export function getCloudRenderingRegionsQueryKey(filters: RegionFilter = {}) {
  return [
    "cloud-rendering-regions",
    fromEntries(entries(filters).filter(([, val]) => val !== undefined)),
  ];
}

export interface RegionsQueryResult {
  regions: CloudRenderingRegion[];
  userPosition: {
    latitude: number;
    longitude: number;
  };
}

export function useCloudRenderingRegionsQuery<T = RegionsQueryResult>({
  filters = {},
  queryOptions,
}: {
  filters?: RegionFilter;
  queryOptions?: Partial<UseQueryOptions<RegionsQueryResult, AxiosError, T>>;
} = {}) {
  const { user } = useAuthentication();

  return useQuery<RegionsQueryResult, AxiosError, T>({
    queryKey: getCloudRenderingRegionsQueryKey(filters),
    queryFn: async () => {
      const response = await getOrderedCloudRenderingRegions({ ...filters });
      return {
        regions: response.regions,
        userPosition: response.userPosition,
      };
    },
    ...queryOptions,
    enabled: !!user && (queryOptions?.enabled ?? true),
    staleTime: 60 * 1000, // 1 minute
  });
}

function getBestVmSize(region?: Region) {
  let vmSize = undefined;
  if (region && region?.supportedVirtualMachineSizes.length > 0) {
    vmSize = region.supportedVirtualMachineSizes[0];
  }
  return vmSize;
}

export type SelectCloudRenderingRegion = {
  region: Region | undefined;
  vmSize: string | undefined;
};

/**
 * Fetches the best cloud rendering regions based on user preferences and other criteria.
 * @param filters additional filter criteria
 * @param queryOptions additional query options
 * @returns a query result containing the best cloud rendering regions
 */
export function useBestCloudRenderingRegionsQuery<T = RegionsQueryResult>({
  filters = {},
  queryOptions,
}: {
  filters?: RegionFilter;
  queryOptions?: Partial<UseQueryOptions<RegionsQueryResult, AxiosError, T>>;
} = {}) {
  const cloudRendering = useAppSelector(selectCloudRendering);
  const userPreference = useUserPreferencesQuery();
  const useXRGatewaySettingsQuery = useUseXRGatewaySettingQuery();

  return useCloudRenderingRegionsQuery({
    filters: {
      latencyInfo: cloudRendering.regionsLatency,
      preferredRegion: userPreference.data?.preferredRenderRegion ?? undefined,
      // DCV is the only streaming protocol, so DCV-capable regions are always required
      requireDCV: true,
      requireXRGateway: useXRGatewaySettingsQuery.data,
      ...filters,
    },
    queryOptions: {
      ...queryOptions,
      enabled:
        userPreference.isSuccess &&
        useXRGatewaySettingsQuery.isSuccess &&
        (queryOptions?.enabled ?? true),
    },
  });
}

/**
 * Fetches the best cloud rendering region based on user preferences and other criteria.
 * @param filters additional filter criteria
 * @returns a query result containing the best cloud rendering region
 */
export function useBestCloudRenderingRegionQuery({
  filters = {},
}: {
  filters?: RegionFilter;
  queryOptions?: Partial<
    UseQueryOptions<SelectCloudRenderingRegion, AxiosError>
  >;
} = {}) {
  const isLatencyCheckCompleted = useAppSelector(selectLatencyTestCompleted);

  return useBestCloudRenderingRegionsQuery({
    filters,
    queryOptions: {
      // only consider enabled regions and only return the first one
      select: (data): SelectCloudRenderingRegion => {
        const regions = data.regions;
        // Find the first enabled region with an available vmSize
        for (const region of regions) {
          if (!region.isEnabled) continue;

          const vmSize = getBestVmSize(region);
          if (!vmSize) continue;

          // Found an available region/vmSize combination
          return { region, vmSize };
        }

        // No available combinations found, return the first enabled region anyway
        const bestRegion = regions.filter((r) => r.isEnabled).at(0);
        return { region: bestRegion, vmSize: getBestVmSize(bestRegion) };
      },
      enabled: isLatencyCheckCompleted,
      // Keep showing the previously resolved region while a new one is being
      // computed after a filter change (e.g. when the user picks a different
      // performance tier). Without this the best region briefly resets to
      // undefined on every filter change, which makes any UI gated on it (like
      // the performance tier selector) disappear until the refetch completes.
      placeholderData: keepPreviousData,
    },
  });
}

/**
 * Fetches a specific cloud rendering region by name.
 * @param name the name of the region to fetch
 * @returns a query result containing the cloud rendering region
 */
export function useCloudRenderingRegionQuery(name?: string) {
  return useCloudRenderingRegionsQuery({
    queryOptions: {
      select: (data) => data.regions?.find((x) => x.name === name),
      enabled: !!name,
    },
  });
}
